import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '@core/redux/hooks';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { IconButton, Divider, Drawer, LinearProgress, Stack, Typography } from '@mui/material';
import { useSnapshot } from 'valtio';
import { TimeEvent, useTimeEvents } from '@core/analytics';
import { CustomScrollContainer, useTools } from '@core/components';
import { setProj4Projection, useKeyCombination, useSelectAndFocusPointsById } from '@core/logic';
import { elementActions, elementThunks, elementTypeThunks } from '@elements/redux';
import { pipeConnectorThunks } from '@elements/redux/connectors.slice';
import { markerSphereThunks } from '@elements/redux/markers.slice';
import { pipeFittingThunks } from '@elements/redux/pipe-fitting.slice';
import { pointElementActions, pointElementThunks } from '@elements/redux/point-element.slice';
import { projectsHistoryActions } from '@menu/redux/projectsHistory';
import { PhotoPreview } from '@photos/components/PhotosInViewport/PhotoPreview';
import { EditModes, editModeState } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { projectThunks } from '@projects/redux';
import { ManualPositionWarningDialog, ScanDetails } from '@scans/components';
import { TransformationReasonDialog } from '@processingResults/components';
import { tagThunks } from '@tags/redux';
import { trenchActions, trenchThunks } from '@trenches/redux';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { OrderNumber, OrderNumberType } from '@projects/types/orderNumbers';
import PhotoDetails from '@photos/components/PhotosInViewport/PhotoDetail';
import {
  openCurrentPhotoInNewTab,
  photosState,
  setSelectedPhoto,
} from '@photos/components/PhotosInViewport/state';
import { ArrowBoxOut } from '@deepup/icons';
import { useSMANumbers } from '@core/logic/hooks/useSMANumbers';
import { MapLayersBox } from './LayerToggles';
import { ProjectMenu } from './menu';
import { ElementPointDetails, TrenchPointDetails } from './PointDetails';
import { SMADetails } from './SmaNumbers/SMADetails';
import { ProjectBar } from './ProjectBar';
import { ProjectCanvas } from './ProjectCanvas';
import { MarkersAndConnectorsSection } from './MarkersAndConnectorsSection';
import { HouseLeadDetails } from './HouseLeadDetails';
import { useScanStats } from './hooks';
import { useScanUrlRouting } from './routing';
import { elementSelectionAreaState } from './menu/ElementOrganization/state';
import { ElementSelectionAreaDetails } from './menu/ElementOrganization/ElementSelectionAreaDetails';
import { PlandataAlert } from './PlandataAlert';

/**
 * This is its own component so React doesn't need to rerender ProjectDetail everytime something changes here (performance!)
 */
const DebugComponent = () => {
  useSelectAndFocusPointsById();
  return null;
};

export const ProjectDetail = () => {
  const dispatch = useAppDispatch();
  const { projectId } = useParams<{ projectId?: string; scanId?: string }>();
  const project = useSelector(viewer3dSelectors.selectedProject);
  const isScanSelected = !!useSelector(viewer3dSelectors.selectedScanId);
  const { mode: selectedEditMode } = useSnapshot(editModeState);
  const { selectedSplineElements } = useSnapshot(elementSelectionAreaState);
  const scanDetailsOpen = isScanSelected && selectedEditMode === EditMode.Scan;
  const selectedPointId = useSelector(viewer3dSelectors.selectedPointId);
  const isPointSelected = !!selectedPointId;
  const elementPointDetailsOpen = isPointSelected && selectedEditMode === EditMode.Element;
  const trenchPointDetailsOpen = isPointSelected && selectedEditMode === EditMode.Trench;

  const { loadedVisiblePercent, loading } = useScanStats();
  useScanUrlRouting();
  const theme = useTheme();
  const leftDrawerWidth = theme.spacing(52);
  const rightDrawerWidth = theme.spacing(46);

  const selectedScan = useSelector(viewer3dSelectors.selectedScan);

  const [smaNumbers, setSmaNumbers] = useState<readonly OrderNumber[]>([]);
  const { selectedPhoto } = useSnapshot(photosState);
  const { fetchSMANumbers } = useSMANumbers();

  useTools();

  useEffect(() => {
    if (!selectedPhoto) {
      setSmaNumbers([]);
      return;
    }

    if (selectedPhoto.orderNumbers) {
      const smaOrders = selectedPhoto.orderNumbers.filter(
        (order) => order.type === OrderNumberType.SMA,
      );
      setSmaNumbers(smaOrders as unknown as readonly OrderNumber[]);
    }
  }, [selectedPhoto?.id, selectedPhoto?.orderNumbers]);

  useEffect(() => {
    let isMounted = true;

    const fetchScanSMANumbers = async () => {
      if (!selectedScan || !projectId) {
        if (isMounted) setSmaNumbers([]);
        return;
      }

      try {
        const smaOrders = await fetchSMANumbers(projectId, selectedScan.id, 'scans');
        if (isMounted) setSmaNumbers(smaOrders);
      } catch (error) {
        console.error('Failed to fetch SMA numbers for scan:', error);
        if (isMounted) setSmaNumbers([]);
      }
    };

    fetchScanSMANumbers();

    return () => {
      isMounted = false;
    };
  }, [selectedScan?.id, projectId, fetchSMANumbers]);

  useEffect(() => {
    if (!projectId) return;
    dispatch(viewer3dActions.setSelectedProjectId(projectId));
    dispatch(viewer3dActions.clearSelection());

    dispatch(
      projectThunks.getOne({
        path: ['projects', projectId],
        params: { projection: 'scanCount' },
      }),
    );
    dispatch(trenchActions.removeAll());
    dispatch(
      trenchThunks.getMany({
        path: ['projects', projectId, 'trenches'],
      }),
    );
    dispatch(elementActions.removeAll());
    dispatch(
      elementThunks.getMany({
        path: ['projects', projectId, 'splineElements'],
      }),
    );
    dispatch(pointElementActions.removeAll());
    dispatch(
      pointElementThunks.getMany({
        path: ['projects', projectId, 'pointElements'],
      }),
    );
    dispatch(
      pipeFittingThunks.getMany({
        path: ['projects', projectId, 'splineElementConnections'],
      }),
    );
    dispatch(
      pipeConnectorThunks.getMany({
        path: ['projects', projectId, 'connectors'],
      }),
    );
    dispatch(
      markerSphereThunks.getMany({
        path: ['projects', projectId, 'markers'],
      }),
    );
    dispatch(
      elementTypeThunks.getMany({
        path: ['splineElementTypes'],
        params: { page: 0, size: 1000 },
      }),
    );
    dispatch(tagThunks.getMany({ path: ['tags'], params: { size: 1000 } }));
  }, [dispatch, projectId]);

  useEffect(() => {
    if (!project?.epsgCode || !project?.proj4Projection) {
      return;
    }
    project && setProj4Projection(project.epsgCode, project.proj4Projection);
  }, [project?.epsgCode, project?.proj4Projection]);

  useEffect(() => {
    if (project) {
      document.title = project.name;
      dispatch(projectsHistoryActions.addToRecent({ id: project.id, title: project.name }));
    }
  }, [project, dispatch]);

  useEffect(() => {
    if (scanDetailsOpen || elementPointDetailsOpen || trenchPointDetailsOpen) {
      setSelectedPhoto(null);
    }
  }, [scanDetailsOpen, elementPointDetailsOpen, trenchPointDetailsOpen]);

  /**
    Last parameter is used to inject additional data to the event payload.
    We use router param instead of project object since it is not available on router navigation
  */
  useTimeEvents([TimeEvent.TimeSpentProjectDetail], [project?.id], { projectId: projectId! });

  const toggleUI = useKeyCombination('Meta', '.');

  if (!project) return null;

  const isLoadingFinished = !loading && (loadedVisiblePercent <= 0 || loadedVisiblePercent >= 100);

  const topAppBarHeight = theme.spacing(6);

  const handleOpenInNewTab = () => {
    if (selectedPhoto) {
      openCurrentPhotoInNewTab();
    }
  };

  const getActiveDetailComponent = () => {
    if (selectedPhoto) {
      return <PhotoDetails />;
    }
    if (scanDetailsOpen) {
      return <ScanDetails />;
    }
    if (elementPointDetailsOpen) {
      return <ElementPointDetails />;
    }
    if (trenchPointDetailsOpen) {
      return (
        <>
          <MarkersAndConnectorsSection selectedPointId={selectedPointId} />
          <Divider />
          <TrenchPointDetails />
        </>
      );
    }
    if (selectedSplineElements.length > 0) {
      return (
        <ElementSelectionAreaDetails
          splineElements={
            selectedSplineElements as unknown as import('@elements/types').SplineElement[]
          }
        />
      );
    }
    return null;
  };

  const activeDetailComponent = getActiveDetailComponent();

  return (
    <>
      <DebugComponent />
      <Stack>
        <LinearProgress
          variant="determinate"
          value={loadedVisiblePercent}
          sx={(theme) => ({
            position: 'absolute',
            left: 0,
            right: 0,
            zIndex: theme.zIndex.drawer + 1,
            opacity: isLoadingFinished ? 0 : 1,
            transition: theme.transitions.create('opacity'),
          })}
        />
        <Drawer
          sx={{ display: toggleUI ? 'none' : 'flex' }}
          variant="permanent"
          anchor="left"
          PaperProps={{
            elevation: 2,
            sx: {
              width: leftDrawerWidth,
              pt: topAppBarHeight, // to prevent the drawer from being occluded by the AppBar
            },
          }}
        >
          <ProjectMenu key={project.id} />
        </Drawer>
        <Drawer
          role="complementary"
          aria-label="details"
          variant="persistent"
          anchor="right"
          open={Boolean(activeDetailComponent)}
          PaperProps={{
            elevation: 2,
            sx: {
              width: rightDrawerWidth,
              pt: topAppBarHeight,
              overflow: 'hidden',
            },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" padding={2}>
            <Typography variant="body1" fontWeight="bold">
              {scanDetailsOpen
                ? 'Selected Scan'
                : elementPointDetailsOpen
                  ? 'Selected Element Point'
                  : trenchPointDetailsOpen
                    ? 'Selected Trench Point'
                    : selectedPhoto
                      ? 'Selected Photo'
                      : null}
            </Typography>
            {selectedPhoto && (
              <IconButton
                onClick={handleOpenInNewTab}
                color="secondary"
                aria-label="open in new tab"
              >
                <ArrowBoxOut />
              </IconButton>
            )}
          </Stack>
          <Divider />
          {activeDetailComponent && (
            <CustomScrollContainer flexGrow={1}>
              {activeDetailComponent}
              <Divider />

              {(selectedPhoto || selectedScan) && (
                <SMADetails
                  smaNumbers={smaNumbers as OrderNumber[]}
                  projectId={projectId}
                  photoId={selectedPhoto?.id}
                  scanId={selectedScan?.id}
                />
              )}
            </CustomScrollContainer>
          )}
        </Drawer>

        {elementPointDetailsOpen && (
          <Stack
            position={'absolute'}
            right={rightDrawerWidth}
            width={rightDrawerWidth}
            bottom={0}
            zIndex={theme.zIndex.snackbar}
          >
            <HouseLeadDetails />
          </Stack>
        )}

        <Stack
          sx={{
            display: toggleUI ? 'none' : 'flex',
            overflow: 'hidden',
            ml: leftDrawerWidth,
            pr:
              scanDetailsOpen || elementPointDetailsOpen || trenchPointDetailsOpen
                ? rightDrawerWidth
                : 0,
            zIndex: theme.zIndex.drawer - 1,
          }}
        >
          <ProjectBar />
          <PlandataAlert />
        </Stack>

        <Stack
          direction="row"
          justifyContent={'start'}
          mt={2}
          ml={leftDrawerWidth}
          pl={2}
          maxWidth={theme.spacing(32)}
          zIndex={theme.zIndex.drawer}
        >
          <MapLayersBox />
        </Stack>

        <PhotoPreview />

        <ProjectCanvas />

        <EditModes />
        <ManualPositionWarningDialog />
        <TransformationReasonDialog />
      </Stack>
    </>
  );
};
